import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import { TResource } from '~/api/oneBoxV3/oneBoxV3.type';
import { isAndroid, isWeb, isMobileDevice, isiOS } from '~/utils/helper/device';
import { forceDownload } from '~/utils/helper/downloadFile';
import streamSaver from 'streamsaver';
import { invokeChannel, onChannel, sendChannel } from '~/utils/constants';
import { FileStatus, TDownloadList } from '~/store/resource/resource.type';
import {
  BatchZipReporter,
  createBatchZipObserver,
  detectStreamSaverMode,
  resolveBatchDownloadName,
  runObservedBatchZip,
} from '~/utils/helper/batchZipObserver';
import { TDownloadableResource } from '~/types/common.type';
import {
  batchZipFileFailedEvent,
  batchZipStalledEvent,
  batchZipStartedEvent,
  batchZipTerminalEvent,
  downloadStartEvent,
} from '~/tracking';

/**
 * @description 判斷檔案以下載 (link) 或網頁預覽 (window) 方式開啟
 */
export enum openMode {
  link = 'link',
  window = 'window',
}

/**
 * 網頁版批次下載以 zip-stream 打包，該實作僅支援傳統 ZIP（32-bit 欄位），
 * 總量超過 4GiB 會靜默溢位、產生損毀的 zip。以 3.8GB（十進位，與 UI
 * 檔案大小顯示同基準）為上限預留安全餘裕，超過時由呼叫端擋下並提示分批。
 * 桌機版由 Electron 主程序逐檔下載、不打包，不受此限。
 */
export const WEB_BATCH_DOWNLOAD_SIZE_LIMIT_BYTES = 3.8 * 1000 ** 3;

export const isWebBatchDownloadOverSizeLimit = (
  list: TDownloadableResource[],
): boolean =>
  isWeb &&
  list.reduce((sum, { resource }) => sum + (resource.file.fileSize || 0), 0) >
    WEB_BATCH_DOWNLOAD_SIZE_LIMIT_BYTES;

type TDownload = { resource: TResource; mode: openMode };

type TDeleteFile = { filePath: string; filename: string };

type TExtract = {
  key?: string;
  filename: string;
  url: string;
  status: FileStatus;
  updateTime: number;
  bytes: number;
  remainingBytes: number;
  keywords: string[];
};

type TMultipleDownload = { list: TDownloadableResource[]; mode: openMode };

type TMultipleUpdate = {
  filePath: string;
  filename: string;
  resource: TResource;
};

/**
 * 批次下載的觀測回報（NBX3-954）。GA 是計數來源（開始、結果、單檔失敗、停滯），
 * Sentry 只做診斷，不拿來算次數：兩邊的阻擋率、去重、重送都不同，數字不能互除。
 * Sentry 的訊息帶 batch_id，讓 Dedupe 不會把連續重試吃掉；分組由 fingerprint 決定。
 */
const batchZipReporter: BatchZipReporter = {
  started: batchZipStartedEvent,
  terminal: batchZipTerminalEvent,
  failure: (error, { stage, kind, fingerprint, extra }) => {
    // 以 handled 方式回報，不要拿掉：NANI-BOX3-47 就是這裡讓我們看見的
    Sentry.captureException(error, {
      tags: {
        feature: 'download',
        batch_zip_stage: stage,
        batch_zip_error_kind: kind,
      },
      fingerprint,
      extra: { type: 'multipleFileDownload', ...extra },
    });
  },
  fileFailed: (payload, extra) => {
    batchZipFileFailedEvent(payload);
    Sentry.captureMessage(
      `batch zip file failed: ${payload.reason} (${payload.batch_id})`,
      {
        level: 'warning',
        tags: { feature: 'download', batch_zip_file_reason: payload.reason },
        fingerprint: ['multipleFileDownload', 'file_failed', payload.reason],
        extra: { type: 'multipleFileDownload', ...extra },
      },
    );
  },
  stalled: (payload, extra) => {
    batchZipStalledEvent(payload);
    Sentry.captureMessage(
      `batch zip stalled at ${payload.stage} (${payload.batch_id})`,
      {
        level: 'warning',
        tags: { feature: 'download', batch_zip_stage: payload.stage },
        fingerprint: ['multipleFileDownload', 'stalled', payload.stage],
        extra: { type: 'multipleFileDownload', ...extra },
      },
    );
  },
};

const desktopHandler = {
  downloadListChangeListener: (
    callback: (event: { downloadList: TDownloadList }) => void,
  ) => {
    window.api.on(onChannel.downloadListChange, ([event]) => {
      callback(event as { downloadList: TDownloadList });
    });
  },
  download: ({ resource, mode }: TDownload) => {
    if (mode === openMode.link || mode === openMode.window) {
      window.api.send(sendChannel.download, resource);
    }
  },
  consoleListener: () => {
    window.api.on(onChannel.console, ([event]) => {
      console.log(event);
    });
  },
  downloadProgressListener: (callback: (event: unknown) => void) => {
    window.api.on(onChannel.downloadProgressing, ([event]) => {
      callback(event);
    });
  },
  getDownloadList: async () => {
    const list = await window.api.invoke(invokeChannel.getDownloadList);
    return list;
  },
  pause: (key: string) => {
    window.api.send(sendChannel.pause, key);
  },
  resume: (key: string) => {
    window.api.send(sendChannel.resume, key);
  },
  cancel: (key: string) => {
    window.api.send(sendChannel.cancel, key);
  },
  deleteFile: (props: TDeleteFile) => {
    window.api.send(sendChannel.deleteFile, props);
  },
  extract: (props: TExtract) => {
    if (props.key) {
      window.api.send(sendChannel.extract, props);
    }
  },
  multipleFileDownload: ({ list, mode }: TMultipleDownload) => {
    if (mode === openMode.link || mode === openMode.window) {
      window.api.send(sendChannel.multipleFileDownload, list);
    }
  },
  update: (props: TDeleteFile & TDownload) => {
    window.api.send(sendChannel.update, props);
  },
  multipleFileUpdate: (props: TDownloadableResource[]) => {
    const info: TMultipleUpdate[] = [];
    props.forEach(({ progress, resource }) => {
      if (progress.key) {
        info.push({
          filePath: progress.key,
          filename: resource.file.fileName,
          resource: resource,
        });
      }
    });
    window.api.send(sendChannel.multipleFileUpdate, info);
  },
  multipleFilePause: (keys: string[]) => {
    window.api.send(sendChannel.multipleFilePause, keys);
  },
  multipleFileResume: (keys: string[]) => {
    window.api.send(sendChannel.multipleFileResume, keys);
  },
};

const webHandlers = {
  /**
   * @description 除了 ios 不可下載大型檔案而需另開網頁預覽，其他系統都是下載
   */
  download: async ({ resource, mode }: TDownload) => {
    const { file, fileName } = resource;
    if (mode === openMode.window) {
      window.open(file.url, '_blank');
    } else if (mode === openMode.link) {
      if (isMobileDevice && isiOS) {
        forceDownload({ url: file.url, name: fileName });
      } else if (isMobileDevice && isAndroid) {
        // browser 遇到無法辨認的檔案形式會直接下載，但可以辨認的就會直接在頁面顯示 => streamSaver
        /**
         * @description step 1: create 一個可以被寫入的文件，後續要下載的檔案會寫入這個文件
         */
        const fileStream = streamSaver.createWriteStream(fileName);

        try {
          // step 2: 透過 fetch 去拜訪 URL，再把內容一段一段放到 step 1 所 create 的文件
          /**
           * @description ReadableStream，可將下載檔案分段處理，由於檔案被切多段，可以做到控制何時開始或取消下載，與網路中斷後斷點續傳
           */
          const res = await fetch(file.url);
          if (!res.body) return;
          const readableStream = res.body;
          if (window.WritableStream) {
            await readableStream.pipeTo(fileStream);
          }

          // step 3: listen document read completed or not, if yes, execute "close and save document"
          const writer = fileStream.getWriter();
          window.writer = writer;
          const reader = res.body.getReader();
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const pump = () =>
            reader
              .read()
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              .then(pumpRes =>
                pumpRes.done
                  ? writer.close()
                  : writer.write(pumpRes.value).then(pump),
              );
        } catch (err) {
          // 使用者觸發的 CDN 下載在行動網路上偶發失敗（fetch 中斷）。
          // 這裡 catch 起來以 handled 方式回報，避免變成 unhandled rejection 洗版 Sentry，
          // 同時保留下載失敗率的可觀測性。
          Sentry.captureException(err, {
            tags: { feature: 'download' },
            extra: { url: file.url, fileName },
          });
        }
      } else {
        forceDownload({ url: file.url, name: fileName });
      }
    }
  },
  downloadProgressListener: () => {},
  getDownloadList: () => {},
  downloadListChangeListener: () => {},
  pause: () => {},
  resume: () => {},
  cancel: () => {},
  deleteFile: () => {},
  extract: () => {},
  multipleFileDownload: ({ list, mode }: TMultipleDownload) => {
    if (mode === openMode.link) {
      const downloadName = resolveBatchDownloadName(list[0].resource);
      const fileStream = streamSaver.createWriteStream(`${downloadName}.zip`);
      const observer = createBatchZipObserver({
        batchId: uuidv4(),
        downloadName,
        list,
        streamSaverMode: detectStreamSaverMode(streamSaver),
        reporter: batchZipReporter,
      });
      // 不 await：檔案抓不到時這一批會卡住、永遠不會結束（見 batchZipObserver.ts）
      void runObservedBatchZip({
        list,
        fetchFn: url => fetch(url),
        writer: fileStream.getWriter(),
        observer,
      });
    } else {
      list.forEach(item => {
        window.open(item.resource.file.url, '_blank');
      });
    }
  },
  consoleListener: () => {},
  update: () => {},
  multipleFileUpdate: () => {},
  multipleFilePause: () => {},
  multipleFileResume: () => {},
};

/**
 * 批次逐檔發 download_start 時，分批延遲送出的參數。
 * 一次同步爆量會撞到瀏覽器 sendBeacon 的 64KiB 共用配額，gtag 會靜默丟掉超出的事件；
 * 分批 + 間隔讓 beacon 配額在每批之間逐步釋放。
 */
const BATCH_START_CHUNK_SIZE = 4;
const BATCH_START_CHUNK_DELAY_MS = 600;

export const useDownload = () => {
  const {
    extract,
    pause,
    resume,
    cancel,
    deleteFile,
    download,
    downloadProgressListener,
    downloadListChangeListener,
    getDownloadList,
    multipleFileDownload: rawMultipleFileDownload,
    consoleListener,
    update,
    multipleFileUpdate,
    multipleFilePause,
    multipleFileResume,
  } = isWeb ? webHandlers : desktopHandler;

  /**
   * 批次下載：逐檔補發 download_start，使其與「每檔一顆」的 download_complete 對齊
   * （Electron 主程序是逐檔下載，批次 N 檔 = N 顆 complete）。
   * download_all_start 仍由呼叫端發送，作為「批次動作」行為指標（不當完成率分母）。
   */
  const multipleFileDownload = ({ list, mode }: TMultipleDownload) => {
    list.forEach(({ resource }, index) => {
      const delay =
        Math.floor(index / BATCH_START_CHUNK_SIZE) * BATCH_START_CHUNK_DELAY_MS;
      setTimeout(() => {
        downloadStartEvent({
          event_type: '批次下載',
          batch_index: index,
          edu: resource.eduCodes?.[0],
          subject: resource.subjects?.[0],
          category: resource.resources1?.[0],
          resources_label: resource.resources2?.[0],
          resources_sub_label: resource.resources3?.[0],
          resources_url: resource.file.url,
          resources_name: resource.fileName,
        });
      }, delay);
    });
    rawMultipleFileDownload({ list, mode });
  };

  return {
    download,
    downloadProgressListener,
    getDownloadList,
    downloadListChangeListener,
    pause,
    resume,
    cancel,
    deleteFile,
    extract,
    multipleFileDownload,
    consoleListener,
    update,
    multipleFileUpdate,
    multipleFilePause,
    multipleFileResume,
  };
};
